import React from 'react';
import { ItemLine, LineInput } from '../site/types';

type LineHandlerProps = {
    itemLines: ItemLine[];
    pdErrorIndices: { [key: string]: boolean };
    onItemLinesChange: (newItemLines: ItemLine[]) => void;
    onPdErrorChange: (newPdErrorIndices: { [key: string]: boolean }) => void;
};

export const LineHandler = ({ itemLines, pdErrorIndices, onItemLinesChange, onPdErrorChange }: LineHandlerProps) => {

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>, lineIndex: number) => {
        const { value } = e.target;
        const updatedLines = itemLines.map((line, idx) =>
            idx === lineIndex ? { ...line, name: value } : line
        );
        onItemLinesChange(updatedLines);
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        lineIndex: number,
        inputIndex: number,
        fieldName: keyof LineInput
    ) => {
        const { value } = e.target;
        const errorKey = `${lineIndex}-${inputIndex}`;
        let newPdErrorIndices = { ...pdErrorIndices };

        if (fieldName === "pd") {
            if (value !== "" && (isNaN(Number(value)) || Number(value) <= 0)) { // Also check for positive number
                newPdErrorIndices[errorKey] = true;
            } else {
                delete newPdErrorIndices[errorKey];
            }
            onPdErrorChange(newPdErrorIndices);
        }

        const updatedLines = itemLines.map((line, lIdx) => {
            if (lIdx === lineIndex) {
                const newInputs = line.inputs.map((input, iIdx) => {
                    if (iIdx === inputIndex) {
                        return { ...input, [fieldName]: value };
                    }
                    return input;
                });
                return { ...line, inputs: newInputs };
            }
            return line;
        });
        onItemLinesChange(updatedLines);
    };

    if (!itemLines || itemLines.length === 0) {
        return <p className="text-gray-500">Adjust quantity to add prescription lines.</p>;
    }

    return (
        <div className="space-y-10">
            {itemLines.map((line, line_index) => (
                <div key={line_index} className="bg-gray-50 p-5 rounded-lg shadow-md space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2 sm:mb-0">
                            Line #{line_index + 1}
                        </h3>
                        <input
                            type="text"
                            value={line.name}
                            required
                            placeholder="Enter name for this prescription"
                            onChange={(e) => handleNameChange(e, line_index)}
                            title="This helps us identify lines and link the prescription to a name."
                            className="px-3 py-2 w-full sm:w-auto border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div className="overflow-x-auto rounded-md">
                        <table className="min-w-full border-collapse border border-gray-300">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Eye</th>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">SPH</th>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">CYL</th>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">AXIS</th>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">ADD</th>
                                    <th className="px-4 py-2 border border-gray-300 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">PD</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {line.inputs.map((input, input_index) => {
                                    const errorKey = `${line_index}-${input_index}`;
                                    return (
                                        <tr key={input_index} className={`${input_index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-4 py-2 border border-gray-300 whitespace-nowrap text-sm font-medium text-gray-800">{input.title}</td>
                                            {(['sph', 'cyl', 'axis', 'add', 'pd'] as (keyof LineInput)[]).map(field => (
                                                <td key={field} className="px-2 py-1 border border-gray-300" data-label={field.toUpperCase()}>
                                                    <input
                                                        type="text" // Consider type="number" for SPH, CYL, AXIS, PD with step control if appropriate
                                                        name={`${field}-${line_index}-${input_index}`} // Unique name for each input
                                                        value={input[field]}
                                                        onChange={(e) => handleInputChange(e, line_index, input_index, field)}
                                                        required={field === 'pd'} // PD is often critical
                                                        className={`w-full px-2 py-1.5 border ${pdErrorIndices[errorKey] && field === 'pd' ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {Object.keys(pdErrorIndices).some(key => key.startsWith(`${line_index}-`) && pdErrorIndices[key]) && (
                        <p className="text-red-600 text-sm mt-1">PD must be a valid positive number.</p>
                    )}
                </div>
            ))}
        </div>
    );
};